<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import type { ComponentPublicInstance } from 'vue'
import IconPromoter from '~/assets/images/engagement/icon-promoter.svg?component'
import IconVenue from '~/assets/images/engagement/icon-venue.svg?component'
import IconVisitor from '~/assets/images/engagement/icon-visitor.svg?component'

import IconCircle from '~/assets/images/engagement/circle-1.svg?component'
import IconCircle2 from '~/assets/images/engagement/circle-2.svg?component'
import IconCircle3 from '~/assets/images/engagement/circle-3.svg?component'

const { t } = useI18n()
const roles = [
  {
    circle: IconCircle,
    icon: IconPromoter,
    titleKey: 'roles.promoter.title',
    class: 'absolute w-89 h-89',
    image: '/images/engagement/venue.png',
    lines: [
      {
        colorKey: 'roles.promoter.line1.color',
        textKey: 'roles.promoter.line1.text',
      },
      {
        colorKey: 'roles.promoter.line2.color',
        textKey: 'roles.promoter.line2.text',
      },
      {
        colorKey: 'roles.promoter.line3.color',
        textKey: 'roles.promoter.line3.text',
      },
    ],
  },
  {
    circle: IconCircle2,
    icon: IconVenue,
    titleKey: 'roles.venue.title',
    class: 'absolute w-102.5 h-102.5',
    image: '/images/engagement/promoter.png',
    lines: [
      {
        colorKey: 'roles.venue.line1.color',
        textKey: 'roles.venue.line1.text',
      },
      {
        colorKey: 'roles.venue.line2.color',
        textKey: 'roles.venue.line2.text',
      },
      {
        colorKey: 'roles.venue.line3.color',
        textKey: 'roles.venue.line3.text',
      },
    ],
  },
  {
    circle: IconCircle3,
    icon: IconVisitor,
    titleKey: 'roles.visitor.title',
    class: 'relative w-117 h-117',
    image: '/images/engagement/visitor.png',
    lines: [
      {
        colorKey: 'roles.visitor.line1.color',
        textKey: 'roles.visitor.line1.text',
      },
      {
        colorKey: 'roles.visitor.line2.color',
        textKey: 'roles.visitor.line2.text',
      },
      {
        colorKey: 'roles.visitor.line3.color',
        textKey: 'roles.visitor.line3.text',
      },
      {
        colorKey: 'roles.visitor.line4.color',
        textKey: 'roles.visitor.line4.text',
      },
    ],
  },
]

const activeIndex = ref(0)
/* no-op computed kept to preserve potential future usage; avoid unused var warnings by naming with underscore */
const _activeRole = computed(() => roles[activeIndex.value]!)
// Per-card display durations in ms; adjust to reading speed if needed
const displayDurations = [8500, 7000, 7000]
let timeoutId: ReturnType<typeof setTimeout> | null = null
const circleEls = ref<HTMLElement[]>([])
const radii = ref<number[]>([])
const orbitRoot = ref<HTMLElement | null>(null)
let rafId: number | null = null
const baseOrbitMs = 10000
let mediaQueryList: MediaQueryList | null = null
let startTimeMs = 0

function startOrbitLoop() {
  if (rafId !== null) return
  startTimeMs = performance.now()
  const tick = () => {
    const elapsed = performance.now() - startTimeMs
    const angle = ((elapsed % baseOrbitMs) / baseOrbitMs) * 360
    orbitRoot.value?.style.setProperty('--angle', `${angle}deg`)
    rafId = requestAnimationFrame(tick)
  }
  rafId = requestAnimationFrame(tick)
}

function stopOrbitLoop() {
  if (rafId !== null) {
    cancelAnimationFrame(rafId)
    rafId = null
  }
  // Reset angle to avoid any residual transforms
  orbitRoot.value?.style.removeProperty('--angle')
}

function setCircleEl(el: Element | ComponentPublicInstance | null, i: number) {
  if (!el) return
  const htmlEl = (el as ComponentPublicInstance).$el
    ? ((el as ComponentPublicInstance).$el as HTMLElement)
    : (el as HTMLElement)
  circleEls.value[i] = htmlEl
}

function scheduleNext() {
  const duration = displayDurations[activeIndex.value] ?? 6000
  timeoutId = setTimeout(() => {
    activeIndex.value = (activeIndex.value + 1) % roles.length
    scheduleNext()
  }, duration)
}

onMounted(() => {
  scheduleNext()
  nextTick(() => {
    radii.value = circleEls.value.map(
      (el) => el.getBoundingClientRect().width / 2
    )
  })
  // Enable orbit animation only on >=1200px
  mediaQueryList = window.matchMedia('(min-width: 1200px)')
  const handleMediaChange = () => {
    if (mediaQueryList?.matches) {
      startOrbitLoop()
    } else {
      stopOrbitLoop()
    }
  }
  mediaQueryList.addEventListener('change', handleMediaChange)
  handleMediaChange()
})

onBeforeUnmount(() => {
  if (timeoutId) {
    clearTimeout(timeoutId)
    timeoutId = null
  }
  if (rafId !== null) {
    cancelAnimationFrame(rafId)
    rafId = null
  }
  if (mediaQueryList) {
    // Best-effort cleanup; handler was inline, so remove all listeners by cloning
    try {
      mediaQueryList = null
    } catch {}
  }
})
</script>

<template>
  <div
    class="pt-19.5 pb-8 min-[1200px]:pb-60 relative overflow-hidden min-[375px]:overflow-visible"
  >
    <p
      class="text-2xl font-semibold tracking-wide max-w-214.5 mx-auto text-center uppercase leading-7 px-5 lg:px-0 bg-gradient-to-r from-[#4d61fd] to-[#bb2bbf] bg-clip-text text-transparent"
    >
      {{ t('locked-and-vested') }}
    </p>
    <div
      ref="orbitRoot"
      class="flex flex-col items-center justify-center relative mt-11 gap-8 min-[1200px]:gap-0"
    >
      <div
        v-for="(role, i) in roles"
        :key="i"
        :ref="(el) => setCircleEl(el, i)"
        :class="role.class"
        class="hidden min-[1200px]:flex"
      >
        <component :is="role.circle" />
      </div>

      <div
        class="absolute inset-0 items-center justify-center hidden min-[1200px]:flex"
      >
        <div
          v-for="(role, i) in roles"
          :key="'dot-' + i"
          :class="['dot-common', 'orbit']"
          :style="{
            opacity: i === activeIndex ? 1 : 0,
            '--r': (radii[i] || 0) + 'px',
          }"
        >
          <div class="payload">
            <div class="upright">
              <NuxtImg :src="role.image" class="w-12.5" />

              <div class="absolute w-78.5">
                <div
                  class="flex flex-col gap-6 p-6 common-gradient relative before:content-[''] before:absolute before:rounded-3xl overflow-hidden backdrop-blur-[1.25rem]"
                  :class="{
                    'role-primary rounded-3xl': i === 0,
                    'role-secondary rounded-3xl': i === 1,
                    'role-tertiary rounded-3xl': i === 2,
                  }"
                >
                  <NuxtImg
                    src="/images/engagement/sparkles.png"
                    class="absolute w-full h-full top-0 left-0 object-none"
                  />
                  <div class="flex flex-row items-center gap-2">
                    <component :is="role.icon" />
                    <p class="font-bold">
                      {{ t(role.titleKey) }}
                    </p>
                  </div>
                  <ul class="flex flex-col gap-1">
                    <li
                      v-for="(line, j) in role.lines"
                      :key="j"
                      class="flex flex-row items-center"
                    >
                      <Icon
                        name="material-symbols:check-rounded"
                        class="text-[#7540ee]"
                      />
                      <span>
                        <span class="text-[#7540ee] text-sm font-semibold">{{
                          t(line.colorKey)
                        }}</span>
                        {{ ' ' }}
                        <span class="text-[#999999] text-xs">{{
                          t(line.textKey)
                        }}</span>
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        class="min-[1200px]:absolute -z-1 before:bg-[#7540EE] before:w-87.5 before:h-87.5 before:blur-[18.75rem] before:content-[''] before:absolute"
      >
        <NuxtImg
          src="/images/engagement/roles.png"
          class="w-87.5 items-center justify-center m-auto hidden min-[1200px]:flex"
        />

        <NuxtImg
          src="/images/engagement/roles-mobile.png"
          class="max-w-78.5 px-5 md:px-0 md:max-w-100 w-full flex items-center justify-center m-auto min-[1200px]:hidden"
        />
      </div>

      <div class="w-full px-5 sm:px-0 flex flex-col gap-6 min-[1200px]:hidden">
        <div
          v-for="(role, i) in roles"
          :key="i"
          class="mx-auto max-w-78.5 w-full sm:max-w-100"
        >
          <div
            class="flex flex-col gap-6 p-6 common-gradient relative before:content-[''] before:absolute before:rounded-3xl overflow-hidden backdrop-blur-[1.25rem]"
            :class="{
              'role-primary rounded-3xl': i === 0,
              'role-secondary rounded-3xl': i === 1,
              'role-tertiary rounded-3xl': i === 2,
            }"
          >
            <NuxtImg
              src="/images/engagement/sparkles.png"
              class="absolute w-full h-full top-0 left-0 object-none"
            />
            <div class="flex flex-row items-center gap-2">
              <component :is="role.icon" />
              <p class="font-bold">{{ t(role.titleKey) }}</p>
            </div>
            <ul class="flex flex-col gap-1">
              <li
                v-for="(line, j) in role.lines"
                :key="j"
                class="flex flex-row items-center"
              >
                <Icon
                  name="material-symbols:check-rounded"
                  class="text-[#7540ee]"
                />
                <span>
                  <span class="text-[#7540ee] text-sm font-semibold">{{
                    t(line.colorKey)
                  }}</span>
                  {{ ' ' }}
                  <span class="text-[#999999] text-xs">{{
                    t(line.textKey)
                  }}</span>
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.dot-common {
  transition: opacity 0.6s ease;
}

.orbit {
  position: absolute;
  transform: rotate(var(--angle, 0deg));
  will-change: transform;
}
.payload {
  transform: translateX(var(--r));
  will-change: transform;
}
.upright {
  transform: rotate(calc(-1 * var(--angle, 0deg)));
  will-change: transform;
}

.fade-enter-active,
.fade-leave-active {
  transition:
    opacity 0.8s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.8s cubic-bezier(0.22, 1, 0.36, 1);
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

.role-primary {
  background:
    radial-gradient(
      67.83% 112.02% at 117.83% 100%,
      rgba(117, 64, 238, 0.61) 0%,
      rgba(0, 0, 0, 0) 100%
    ),
    linear-gradient(0deg, rgba(81, 81, 81, 0.2), rgba(81, 81, 81, 0.2));
  &::before {
    background:
      radial-gradient(
        43.03% 78.43% at 100.96% 102.12%,
        #7540ee 0%,
        rgba(153, 153, 153, 0) 100%
      ),
      linear-gradient(
        0deg,
        rgba(255, 255, 255, 0.06),
        rgba(255, 255, 255, 0.06)
      );
  }
}

.role-tertiary {
  background:
    radial-gradient(
      68.47% 103.17% at 112.1% -5.52%,
      rgba(117, 64, 238, 0.61) 0%,
      rgba(0, 0, 0, 0) 100%
    ),
    linear-gradient(0deg, rgba(81, 81, 81, 0.2), rgba(81, 81, 81, 0.2));

  &::before {
    background:
      radial-gradient(
        68.57% 69.97% at 103.82% 0%,
        #7540ee 0%,
        rgba(153, 153, 153, 0) 100%
      ),
      linear-gradient(
        0deg,
        rgba(255, 255, 255, 0.06),
        rgba(255, 255, 255, 0.06)
      );
  }
}

.role-secondary {
  background:
    radial-gradient(
      72.61% 119.37% at -6.05% -18.87%,
      rgba(117, 64, 238, 0.61) 0%,
      rgba(0, 0, 0, 0) 100%
    ),
    linear-gradient(0deg, rgba(81, 81, 81, 0.2), rgba(81, 81, 81, 0.2));

  &::before {
    background:
      radial-gradient(
        50.55% 169.05% at 4.14% 16.35%,
        #7540ee 0%,
        rgba(153, 153, 153, 0) 100%
      ),
      linear-gradient(
        0deg,
        rgba(255, 255, 255, 0.06),
        rgba(255, 255, 255, 0.06)
      );
  }
}
</style>
